import { ReactElement, useMemo, useRef } from 'react';
import styles from './ui-filter.module.scss';
import {
    UIErrorState,
    UIIcon,
    UIIconName,
    UILoadingState,
    UIMenu,
    UIMenuDivider,
    UIMenuGroupLabel,
    UIMenuItem,
    UIPopover,
    UIPopoverMountSensor,
    UIPopoverPlacement,
    UIPropoverRef,
    UISearchInput,
    UIText
} from '..';
import { UISpinner } from '../..';
import { UIFilterHeader } from './ui-filter-header';
import { blue, charcoal, red } from '../../styles';
import { useInfiniteScrollSentinel } from '../../hooks';

export type UIMultiFilterGroupItem = {
    name: string;
    id: string;
    selected: boolean;
};

/**
 * Turns a group into a server-paginated one: a search box under the group
 * label, and the next page loaded when the group's last row scrolls into view.
 * The caller owns the search term and is responsible for keeping selected rows
 * in `items` — they may no longer be in the loaded window.
 */
export type UIMultiFilterGroupPaging = {
    search: string;
    onSearchChange: (query: string) => void;
    searchPlaceholder: string;
    errorText: string;
    isLoading: boolean;
    isError: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
    onRetry?: () => void;
};

export type UIMultiFilterGroup = {
    name: string;
    type: 'multi' | 'single';
    emptyText?: string;
    items: UIMultiFilterGroupItem[];
    paging?: UIMultiFilterGroupPaging;
};

type UIMultiFilterItemProps = {
    text: string;
    groups: UIMultiFilterGroup[];
    icon?: UIIconName;
    selectedCount?: number;
    popoverPlacement?: UIPopoverPlacement;
    popoverWidth?: number;
    /** Fired when the dropdown opens and closes, so callers can latch fetching. */
    onOpenChanged?: (open: boolean) => void;
    onChanged: (groups: UIMultiFilterGroup[]) => void;
};

export function UIMultiFilterItem(props: UIMultiFilterItemProps): ReactElement {
    const {
        text,
        groups,
        icon,
        selectedCount = 0,
        popoverPlacement,
        popoverWidth = 200,
        onOpenChanged,
        onChanged
    } = props;

    const ref = useRef<UIPropoverRef>(null);

    function onClickOutside() {
        ref.current?.close();
    }

    const iconNode = useMemo(() => {
        if (icon) {
            return <UIIcon name={icon} size={18} color={charcoal} />;
        } else {
            return undefined;
        }
    }, [icon]);

    return (
        <UIPopover
            ref={ref}
            popoverPadding={8}
            width={popoverWidth}
            placement={popoverPlacement}
            triggerNode={
                <button className={styles.buttonContainer}>
                    <UIFilterHeader
                        text={text}
                        icon={iconNode}
                        pillText={
                            selectedCount > 0 ? `${selectedCount}` : undefined
                        }
                        pillBackgroundColor={red}
                        pillTextColor="white"
                        pillPosition="right"
                    />
                </button>
            }
            onClickOutside={onClickOutside}
        >
            <UIPopoverMountSensor
                onMount={() => onOpenChanged?.(true)}
                onUnmount={() => onOpenChanged?.(false)}
            />
            <UIMenu scroll maxHeight={400}>
                {groups.map((group, index) => (
                    <RenderGroup
                        key={index}
                        group={group}
                        isLast={index >= groups.length - 1}
                        onChanged={(group) => {
                            onChanged(
                                groups.map((g, i) => (i === index ? group : g))
                            );
                        }}
                    />
                ))}
            </UIMenu>
        </UIPopover>
    );
}

function RenderGroup(props: {
    group: UIMultiFilterGroup;
    isLast: boolean;
    onChanged: (group: UIMultiFilterGroup) => void;
}): ReactElement {
    const { group, isLast, onChanged } = props;
    const { paging } = group;

    const { ref: bottomRef } = useInfiniteScrollSentinel({
        hasNextPage: paging?.hasNextPage ?? false,
        isFetchingNextPage: paging?.isFetchingNextPage ?? false,
        onLoadMore: paging?.onLoadMore ?? noop
    });

    const isLoading = paging?.isLoading ?? false;
    const isError = paging?.isError ?? false;
    const showItems = !isLoading && !isError;

    return (
        <>
            <UIMenuGroupLabel text={group.name} />
            {paging && (
                <div style={{ padding: 4 }}>
                    <UISearchInput
                        value={paging.search}
                        placeholder={paging.searchPlaceholder}
                        onChange={paging.onSearchChange}
                    />
                </div>
            )}
            {isLoading && (
                <GroupStateContainer>
                    <UILoadingState />
                </GroupStateContainer>
            )}
            {isError && paging && (
                <GroupStateContainer>
                    <UIErrorState
                        title={paging.errorText}
                        onRetry={paging.onRetry}
                    />
                </GroupStateContainer>
            )}
            {showItems && group.items.length === 0 && group.emptyText && (
                <GroupStateContainer>
                    <UIText>{group.emptyText}</UIText>
                </GroupStateContainer>
            )}
            {showItems &&
                (group.type === 'single' ? (
                    <RenderSingleSelectItems
                        items={group.items}
                        onChanged={(items) => {
                            onChanged({ ...group, items });
                        }}
                    />
                ) : (
                    <RenderMultiSelectItems
                        items={group.items}
                        onChanged={(items) => {
                            onChanged({ ...group, items });
                        }}
                    />
                ))}
            {showItems && paging?.hasNextPage && (
                <div
                    ref={bottomRef}
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: 8
                    }}
                >
                    {paging.isFetchingNextPage && (
                        <UISpinner color={charcoal} />
                    )}
                </div>
            )}
            {!isLast && <UIMenuDivider />}
        </>
    );
}

function noop(): void {}

function GroupStateContainer(props: { children: ReactElement }): ReactElement {
    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                padding: 8
            }}
        >
            {props.children}
        </div>
    );
}

function RenderSingleSelectItems(props: {
    items: UIMultiFilterGroupItem[];
    onChanged: (items: UIMultiFilterGroupItem[]) => void;
}): ReactElement {
    const { items, onChanged } = props;

    return (
        <>
            {items.map((item) => {
                return (
                    <UIMenuItem
                        key={item.name}
                        text={item.name}
                        onClick={() => {
                            const newItems = items.map((i) => {
                                i.selected = i === item;
                                return i;
                            });
                            onChanged(newItems);
                        }}
                        small
                        leftIconName={'check'}
                        leftIconColor={blue}
                        leftIconOpacity={item.selected ? 1 : 0}
                    />
                );
            })}
        </>
    );
}

function RenderMultiSelectItems(props: {
    items: UIMultiFilterGroupItem[];
    onChanged: (items: UIMultiFilterGroupItem[]) => void;
}): ReactElement {
    const { items, onChanged } = props;

    return (
        <>
            {items.map((item) => {
                return (
                    <UIMenuItem
                        key={item.name}
                        text={item.name}
                        onClick={() => {
                            item.selected = !item.selected;
                            onChanged(items);
                        }}
                        checkboxPosition="left"
                        checkboxProps={{
                            checked: item.selected,
                            onChange: (checked) => {
                                item.selected = checked;
                                onChanged(items);
                            }
                        }}
                        small
                    />
                );
            })}
        </>
    );
}
